import React, { useEffect, useState } from "react";
import {
  convertUnitsFinal,
  disposalMethodCode,
} from "../../../../utils/dataAndFormulas/wasteForms";
import { useLanguage } from "../../../../contexts/LanguageContext";
import { formatWhole, formatPercentage } from "../../../../utils/numberFormat";
import "./WasteSummary.scss";

const safeNumber = (v) => (Number.isFinite(v) ? v : 0);

const safeConvert = (value, unit, targetUnit) =>
  safeNumber(convertUnitsFinal(value, unit, targetUnit));

const WasteSummary = ({
  filters,
  site,
  valueBaseline,
  wasteController,
  totales,
  totalesMethods,
  selectorSlot,
  exportSlot,
}) => {

    const { translations } = useLanguage();
    const t = (key) => translations[key] || key;

  const [data, setData] = useState({
    total: 0,
    no_peligrosos: 0,
    peligrosos: 0,
    reciclables: 0,
    porcentaje: 0,
  });

  const [results, setResults] = useState({});

  // 🔹 Función auxiliar para convertir a gramos si el valor es muy pequeño
  const adjustUnitForSmallValues = (value, unit = "t") => {
    if (!Number.isFinite(value)) return { value: 0, unit };

    // Convertir todo a kg primero para comparar
    let valueInKg = value;
    if (unit === "t") valueInKg = value * 1000;
    if (unit === "g") valueInKg = value / 1000;

    // Si es menos de 1 kg, mostrar en gramos
    if (valueInKg < 1) {
      return {
        value: valueInKg * 1000,
        unit: "g",
      };
    }

    // Si es menos de 1000 kg (1 t), mostrar en kg
    if (valueInKg < 1000) {
      return {
        value: valueInKg,
        unit: "kg",
      };
    }

    // Caso contrario, tambien en kg (no sube a toneladas)
    return {
      value: valueInKg,
      unit: "kg",
    };
  };

  // KPIrnp16, KPIrnp17, KPIrnp18, KPIrep19, KPIrep20
  const VALORIZADOS_METHODS = [
    "reuse_recycling_rnp",
    "composting_rnp",
    "energy_recovery_rnp",
    "reuse_recycling_rep",
    "energy_recovery_rep",
  ];

  const isValorizadoMethod = (methodName) =>
    VALORIZADOS_METHODS.includes(methodName);

  const getMethodTotalTon = (methodData, year) => {
    if (!methodData?.[year]) return 0;
    const { total = 0, unit = "kg" } = methodData[year];
    return safeConvert(total, unit, "t");
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!totales || Object.keys(totales).length === 0) return;

        setResults(totales);

        // ---------- TOTALES PELIGROSOS / NO PELIGROSOS ----------
        const unitHazardous =
          totales?.total_hazardous_waste_kpis?.[filters.year]?.unit || "t";
        const unitNonHazardous =
          totales?.total_non_hazardous_waste_kpis?.[filters.year]?.unit || "t";

        const rawHazardous =
          totales?.total_hazardous_waste_kpis?.[filters.year]?.total;
        const rawNonHazardous =
          totales?.total_non_hazardous_waste_kpis?.[filters.year]?.total;

        // console.log("🔍 Debug conversión peligrosos:");
        // console.log("  rawHazardous:", rawHazardous);
        // console.log("  unitHazardous:", unitHazardous);
        // console.log("  rawNonHazardous:", rawNonHazardous);
        // console.log("  unitNonHazardous:", unitNonHazardous);

        const peligrosos = safeConvert(rawHazardous, unitHazardous, "t");
        const no_peligrosos = safeConvert(
          rawNonHazardous,
          unitNonHazardous,
          "t",
        );

        // console.log("  ✅ peligrosos convertido:", peligrosos);
        // console.log("  ✅ no_peligrosos convertido:", no_peligrosos);
        // console.log(filters, rawHazardous, peligrosos, no_peligrosos);
        const total = peligrosos + no_peligrosos;

        // ---------- RECICLADO / RECUPERADO ----------
        // ( KPIrnp16 + KPIrnp17 + KPIrnp18 ) + ( KPIrep19 + KPIrep20 ) / Residuos totales
        let totalValorizadoTon = 0;

        Object.values(totalesMethods || {}).forEach((wasteMethods) => {
          Object.entries(wasteMethods).forEach(([methodName, methodData]) => {
            if (isValorizadoMethod(methodName)) {
              totalValorizadoTon += getMethodTotalTon(methodData, filters.year);
            }
          });
        });

        const porcentajeValorizado =
          total > 0 ? (totalValorizadoTon / total) * 100 : 0;

        // ---------- SET FINAL ----------
        setData({
          total: safeNumber(total),
          no_peligrosos: safeNumber(no_peligrosos),
          peligrosos: safeNumber(peligrosos),
          reciclables: safeNumber(totalValorizadoTon),
          porcentaje: safeNumber(porcentajeValorizado),
        });
      } catch (error) {
        console.error("❌ Error en WasteSummary:", error);
      }
    };

    fetchData();
  }, [filters, site, valueBaseline, wasteController, totales]);

  // ---------- PORCENTAJES VISUALES ----------
  const percentNoPeligrosos =
    data.total > 0 ? (data.no_peligrosos / data.total) * 100 : 0;

  const percentPeligrosos =
    data.total > 0 ? (data.peligrosos / data.total) * 100 : 0;

  const resumenItems = [
    {
      title: t("total_waste"),
      value: data.total,
      unit: "t",
      hasProgress: false,
    },
    {
      title: t("non_hazardous_waste"),
      value: data.no_peligrosos,
      unit: "t",
      secondary: formatPercentage(percentNoPeligrosos),
      progress: percentNoPeligrosos,
      color: "#ffffff",
      hasProgress: true,
    },
    {
      title: t("hazardous_waste"),
      value: data.peligrosos,
      unit: "t",
      secondary: formatPercentage(percentPeligrosos),
      progress: percentPeligrosos,
      color: "#ffffff",
      hasProgress: true,
    },
    {
      title:t("recycled_recovered") ,
      value: data.reciclables,
      unit: "t",
      secondary: formatPercentage(data.porcentaje),
      progress: data.porcentaje,
      color: "#ffffff",
      hasProgress: true,
    },
  ];

  // ---------- RENDER ----------
  return (
    <div className="resumen-grid">
      {resumenItems.map((item, index) => {
        const adjusted = index === 3
          ? { value: item.value * 1000, unit: "kg" }
          : adjustUnitForSmallValues(item.value, item.unit);
        return (
          <div
            key={index}
            className={`resumen-card-col${index === 0 ? " resumen-card-col--first" : ""}`}
          >
            <div className={`resumen-slot-row${index === resumenItems.length - 1 ? " resumen-slot-row--right" : ""}`}>
              {index === 0 && selectorSlot}
              {index === resumenItems.length - 1 && exportSlot}
            </div>
            <div
              className={`resumen-card waste-card ${
                !item.hasProgress ? "no-progress" : ""
              }`}
            >
              <div className="card-meta">
                <div className="card-title-row">
                  <span className="card-title">{item.title}</span>
                </div>
              </div>

              <div className="card-header">
                <span className="card-value">
                  {formatWhole(adjusted.value)}
                  <span className="card-unit">{adjusted.unit}</span>
                </span>
                {item.secondary && (
                  <div className="percentage-text">{item.secondary}</div>
                )}
              </div>

              {item.hasProgress && (
                <div className="progress-container">
                  <div className="progress-bar">
                    <div
                      className="progress-fill"
                      style={{
                        width: `${Math.round(item.progress || 0)}%`,
                        backgroundColor: "#648EBC",
                      }}
                    />
                  </div>
                </div>
              )}

              <div className="empty-space"></div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default WasteSummary;
